import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Users, 
  UserCheck, 
  TrendingUp, 
  DollarSign,
  Award,
  Clock,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_COLORS = {
  bronze: "#CD7F32",
  silver: "#C0C0C0",
  gold: "#FFD700",
  platinum: "#E5E4E2"
};

const AdminOverview = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const response = await axios.get(`${API}/admin/overview`, { withCredentials: true });
        setData(response.data);
      } catch (error) {
        toast.error("Failed to load overview data");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOverview();
  }, []);

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64 bg-slate-700" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 bg-slate-700" />
          ))}
        </div>
      </div>
    );
  }

  const tierData = data ? [
    { name: "Bronze", value: data.partners.by_tier.bronze, color: TIER_COLORS.bronze },
    { name: "Silver", value: data.partners.by_tier.silver, color: TIER_COLORS.silver },
    { name: "Gold", value: data.partners.by_tier.gold, color: TIER_COLORS.gold },
    { name: "Platinum", value: data.partners.by_tier.platinum, color: TIER_COLORS.platinum },
  ].filter(t => t.value > 0) : [];

  const referralData = data ? [
    { name: "Active", value: data.referrals.active, color: "#10B981" },
    { name: "Pending", value: data.referrals.pending, color: "#F59E0B" },
    { name: "Churned", value: data.referrals.churned, color: "#EF4444" },
  ].filter(t => t.value > 0) : [];

  const metrics = data ? [
    {
      title: "Total Partners",
      value: data.partners.total,
      subtitle: `+${data.partners.recent_signups} this month`,
      icon: Users,
      color: "text-blue-400",
      bgColor: "bg-blue-500/10"
    },
    {
      title: "Total Referrals",
      value: data.referrals.total,
      subtitle: `${data.referrals.active} active`,
      icon: UserCheck,
      color: "text-emerald-400",
      bgColor: "bg-emerald-500/10"
    },
    {
      title: "Monthly MRR",
      value: `$${data.commissions.total_mrr.toLocaleString()}`,
      subtitle: "Recurring commission",
      icon: TrendingUp,
      color: "text-purple-400",
      bgColor: "bg-purple-500/10"
    },
    {
      title: "Total Earnings",
      value: `$${data.commissions.total_earnings.toLocaleString()}`,
      subtitle: `$${data.commissions.pending.toLocaleString()} pending`,
      icon: DollarSign,
      color: "text-amber-400",
      bgColor: "bg-amber-500/10"
    },
  ] : [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Admin Overview</h1>
          <p className="text-slate-400 mt-1">Monitor your partner program performance</p>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, idx) => {
          const Icon = metric.icon;
          return (
            <Card key={idx} className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-400">{metric.title}</p>
                    <p className="text-2xl font-bold text-white mt-1">{metric.value}</p>
                    <p className="text-xs text-slate-500 mt-1">{metric.subtitle}</p>
                  </div>
                  <div className={`p-3 rounded-xl ${metric.bgColor}`}>
                    <Icon className={`w-5 h-5 ${metric.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tier Distribution */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-400" />
              Partner Tier Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-8">
              <div className="w-48 h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={tierData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {tierData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                      itemStyle={{ color: '#fff' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                {tierData.map((tier, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tier.color }} />
                    <span className="text-slate-300 text-sm">{tier.name}</span>
                    <span className="text-white font-medium">{tier.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referral Status */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-400" />
              Referral Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-8">
              <div className="w-48 h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={referralData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {referralData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                      itemStyle={{ color: '#fff' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                {referralData.map((status, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: status.color }} />
                    <span className="text-slate-300 text-sm">{status.name}</span>
                    <span className="text-white font-medium">{status.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commission Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <ArrowUpRight className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Paid Commissions</p>
                <p className="text-2xl font-bold text-emerald-400">
                  ${data?.commissions.paid.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-500/10">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Pending Commissions</p>
                <p className="text-2xl font-bold text-amber-400">
                  ${data?.commissions.pending.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <Link to="/admin/partners" className="flex items-center gap-4 hover:opacity-80 transition-opacity">
              <div className="p-3 rounded-xl bg-purple-500/10">
                <Users className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">View All Partners</p>
                <p className="text-lg font-bold text-purple-400 flex items-center gap-1">
                  {data?.partners.total || 0} Partners
                  <ArrowUpRight className="w-4 h-4" />
                </p>
              </div>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminOverview;
